import { createEffect, createMemo, onCleanup } from "solid-js";
import type { Todo } from "@listto/powersync";
import { MAX_STAGGER_DELAY_MS } from "../components/todo-item";

const INITIAL_LOAD_DURATION_MS = 300;

// Module-level Set to track which lists have already animated in the current
// navigation session. Exported so TodoListView can read it on initialization.
export const animatedLists = new Set<string>();

interface UseInitialAnimationOptions {
  listId: () => string;
  hasAnimatedAccessor: () => boolean;
  setHasAnimated: (val: boolean) => void;
  currentListTodosAccessor: () => Todo[] | undefined;
}

export const useInitialAnimation = (options: UseInitialAnimationOptions) => {
  // Boolean (not the array) so this effect only re-runs when data first
  // *appears* — or the list empties — never on every refetch. A single
  // mutation triggers a burst of PowerSync invalidations (≈6 refetches); if we
  // tracked the array each one would reset the timer below, holding
  // `isInitialLoad` true and re-playing the staggered entrance → visible
  // flicker. Tracking only the boolean keeps the entrance a one-shot.
  const hasTodos = createMemo(() => (options.currentListTodosAccessor()?.length ?? 0) > 0);

  createEffect(() => {
    // Track the list id so the entrance re-arms when navigating to a new list.
    options.listId();
    if (!hasTodos() || options.hasAnimatedAccessor()) {
      return;
    }
    const timer = setTimeout(() => {
      animatedLists.add(options.listId());
      options.setHasAnimated(true);
    }, INITIAL_LOAD_DURATION_MS + MAX_STAGGER_DELAY_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });
};
